import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Admin ➜ Store
 *
 * Dedicated page for editing the 8 credit packs and 3 upgrade packs shown on
 * /buy-credits and /buy-upgrades. Each pack has its own external checkout URL
 * that admins paste here. Supports placeholders substituted per buyer in
 * `/app/frontend/src/lib/checkoutUrl.js`.
 */
export default function AdminStore() {
  const [settings, setSettings] = useState({ credit_packages: [], upgrade_packs: [], banner_packages: [], text_ad_packages: [] });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        if (!cancelled) setSettings({
          credit_packages: Array.isArray(data?.credit_packages) ? data.credit_packages : [],
          upgrade_packs: Array.isArray(data?.upgrade_packs) ? data.upgrade_packs : [],
          banner_packages: Array.isArray(data?.banner_packages) ? data.banner_packages : [],
          text_ad_packages: Array.isArray(data?.text_ad_packages) ? data.text_ad_packages : [],
        });
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateCredit = (i, patch) => {
    const arr = [...(settings.credit_packages || [])];
    arr[i] = { ...arr[i], ...patch };
    setSettings((s) => ({ ...s, credit_packages: arr }));
  };
  const updateUpgrade = (i, patch) => {
    const arr = [...(settings.upgrade_packs || [])];
    arr[i] = { ...arr[i], ...patch };
    setSettings((s) => ({ ...s, upgrade_packs: arr }));
  };
  const updateBanner = (i, patch) => {
    const arr = [...(settings.banner_packages || [])];
    arr[i] = { ...arr[i], ...patch };
    setSettings((s) => ({ ...s, banner_packages: arr }));
  };
  const updateTextAd = (i, patch) => {
    const arr = [...(settings.text_ad_packages || [])];
    arr[i] = { ...arr[i], ...patch };
    setSettings((s) => ({ ...s, text_ad_packages: arr }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        credit_packages: settings.credit_packages,
        upgrade_packs: settings.upgrade_packs,
        banner_packages: settings.banner_packages,
        text_ad_packages: settings.text_ad_packages,
      });
      toast.success("Store settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-16 text-center text-slate-500">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Store links
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            External checkout URLs shown to buyers on <code>/buy-credits</code> and <code>/buy-upgrades</code>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/buy-credits"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 dark:text-blue-400 hover:underline"
          >
            Preview Buy Credits <ArrowSquareOut size={12} weight="bold" />
          </a>
          <a
            href="/buy-upgrades"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 dark:text-blue-400 hover:underline"
          >
            Preview Buy Upgrades <ArrowSquareOut size={12} weight="bold" />
          </a>
          <a
            href="/buy-banners"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 dark:text-blue-400 hover:underline"
          >
            Preview Buy Banners <ArrowSquareOut size={12} weight="bold" />
          </a>
          <a
            href="/buy-text-ads"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 dark:text-blue-400 hover:underline"
          >
            Preview Buy Text Ads <ArrowSquareOut size={12} weight="bold" />
          </a>
          <button
            data-testid="store-save-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-5 py-2.5 transition-colors duration-200 disabled:opacity-60"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Credit packs */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Credit packs (8)</div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Each pack has its own checkout URL. Leave the URL blank to show a
            &quot;coming soon&quot; toast for that pack. URLs are used <strong>exactly as saved</strong> — nothing is auto-appended.
            If you want the buyer&apos;s info inside the URL, type the placeholder yourself:{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{username}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{user_id}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{email}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{pack_id}"}</code> or{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{credits}"}</code>.
          </p>
          <div className="space-y-3">
            {(settings.credit_packages || []).map((p, i) => (
              <div key={p.id || `cp-${i}`} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Name</label>
                  <input
                    data-testid={`store-credit-pack-name-${i}`}
                    value={p.name || ""}
                    onChange={(e) => updateCredit(i, { name: e.target.value })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Credits</label>
                  <input
                    data-testid={`store-credit-pack-credits-${i}`}
                    type="number"
                    value={p.credits ?? 0}
                    onChange={(e) => updateCredit(i, { credits: Number(e.target.value) })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Price USD</label>
                  <input
                    data-testid={`store-credit-pack-price-${i}`}
                    type="number"
                    step="0.01"
                    value={p.price_usd ?? 0}
                    onChange={(e) => updateCredit(i, { price_usd: Number(e.target.value) })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Checkout URL</label>
                  <input
                    data-testid={`store-credit-pack-url-${i}`}
                    type="url"
                    placeholder="https://your-shop.com/checkout/…"
                    value={p.url || ""}
                    onChange={(e) => updateCredit(i, { url: e.target.value })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Upgrade packs */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Upgrade packs (3)</div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Membership tiers with an external checkout URL each. Features accept a
            comma-separated list. URLs are used <strong>exactly as saved</strong> — nothing is auto-appended.
            To embed the buyer&apos;s info, type the placeholder yourself in the URL:{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{username}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{user_id}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{email}"}</code> or{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{plan}"}</code>.
          </p>
          <div className="space-y-3">
            {(settings.upgrade_packs || []).map((p, i) => (
              <div key={p.id || `up-${i}`} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Name</label>
                  <input
                    data-testid={`store-upgrade-pack-name-${i}`}
                    value={p.name || ""}
                    onChange={(e) => updateUpgrade(i, { name: e.target.value })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Price USD</label>
                  <input
                    data-testid={`store-upgrade-pack-price-${i}`}
                    type="number"
                    step="0.01"
                    value={p.price_usd ?? 0}
                    onChange={(e) => updateUpgrade(i, { price_usd: Number(e.target.value) })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Period</label>
                  <input
                    data-testid={`store-upgrade-pack-period-${i}`}
                    value={p.period || "month"}
                    onChange={(e) => updateUpgrade(i, { period: e.target.value })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-6">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Checkout URL</label>
                  <input
                    data-testid={`store-upgrade-pack-url-${i}`}
                    type="url"
                    placeholder="https://your-shop.com/checkout/…"
                    value={p.url || ""}
                    onChange={(e) => updateUpgrade(i, { url: e.target.value })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-12">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Features (comma separated)</label>
                  <input
                    data-testid={`store-upgrade-pack-features-${i}`}
                    value={Array.isArray(p.features) ? p.features.join(", ") : (p.features || "")}
                    onChange={(e) => updateUpgrade(i, { features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Banner packs */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Banner impression packs</div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Each pack has its own checkout URL shown on <code>/buy-banners</code>. Leave a URL blank
            to hide the pack behind a &quot;coming soon&quot; toast. URLs are used <strong>exactly as saved</strong> — nothing is auto-appended.
            Placeholders you can drop into the URL:{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{username}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{user_id}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{email}"}</code>,{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{pack_id}"}</code> or{" "}
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5">{"{impressions}"}</code>.
          </p>
          <div className="space-y-3">
            {(settings.banner_packages || []).map((p, i) => (
              <div key={p.id || `bp-${i}`} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Name</label>
                  <input
                    data-testid={`store-banner-pack-name-${i}`}
                    value={p.name || ""}
                    onChange={(e) => updateBanner(i, { name: e.target.value })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Impressions</label>
                  <input
                    data-testid={`store-banner-pack-impressions-${i}`}
                    type="number"
                    value={p.impressions ?? 0}
                    onChange={(e) => updateBanner(i, { impressions: Number(e.target.value) })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Price USD</label>
                  <input
                    data-testid={`store-banner-pack-price-${i}`}
                    type="number"
                    step="0.01"
                    value={p.price_usd ?? 0}
                    onChange={(e) => updateBanner(i, { price_usd: Number(e.target.value) })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Checkout URL</label>
                  <input
                    data-testid={`store-banner-pack-url-${i}`}
                    type="url"
                    placeholder="https://your-shop.com/checkout/…"
                    value={p.url || ""}
                    onChange={(e) => updateBanner(i, { url: e.target.value })}
                    className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Text ad packs */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Text ad impression packs</div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Same placeholder tokens as the other packs (<code>{"{username}"}</code>, <code>{"{user_id}"}</code>,
            <code>{"{email}"}</code>, <code>{"{pack_id}"}</code>, <code>{"{impressions}"}</code>). Leave a URL blank
            to hide the pack behind a "coming soon" toast.
          </p>
          <div className="space-y-3">
            {(settings.text_ad_packages || []).map((p, i) => (
              <div key={p.id || `tp-${i}`} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Name</label>
                  <input data-testid={`store-textad-pack-name-${i}`} value={p.name || ""} onChange={(e) => updateTextAd(i, { name: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Impressions</label>
                  <input data-testid={`store-textad-pack-impressions-${i}`} type="number" value={p.impressions ?? 0} onChange={(e) => updateTextAd(i, { impressions: Number(e.target.value) })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Price USD</label>
                  <input data-testid={`store-textad-pack-price-${i}`} type="number" step="0.01" value={p.price_usd ?? 0} onChange={(e) => updateTextAd(i, { price_usd: Number(e.target.value) })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                </div>
                <div className="col-span-5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Checkout URL</label>
                  <input data-testid={`store-textad-pack-url-${i}`} type="url" placeholder="https://your-shop.com/checkout/…" value={p.url || ""} onChange={(e) => updateTextAd(i, { url: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
